import { useEffect, useRef, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Download, ImageIcon, X } from "lucide-react";
import { api, API } from "@/lib/api";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import { StripArrow, scrollStrip } from "@/components/StripArrow";
import { CardShareButtons } from "@/components/CardShareButtons";
import { useDocuments, VariantButton } from "@/components/DocumentsSection";

export const cardImg = (c, size) => `${API}/cards/${c.id}/image?size=${size}&v=${encodeURIComponent(c.updated_at || c.created_at || "")}`;

export const useCards = () => useQuery({ queryKey: ["cards"], queryFn: () => api.get("/cards").then((r) => r.data) });

const PLACEHOLDERS = Array.from({ length: 6 }, (_, i) => ({ id: `ph-${i}`, placeholder: true }));

const Tile = ({ c, onOpen }) =>
  c.placeholder ? (
    <div data-testid="talking-card-placeholder" className="w-52 sm:w-60 aspect-[5/7] flex-shrink-0 mx-2 snap-center rounded-xl border-2 border-dashed border-[#1B2A41]/25 bg-[#FAF8F2] flex flex-col items-center justify-center text-[#5C6A79] gap-2">
      <ImageIcon className="w-7 h-7 text-[#8FA58A]" />
      <span className="font-type text-[11px] uppercase tracking-widest">Talking card</span>
      <span className="text-[11px]">coming soon</span>
    </div>
  ) : (
    <button type="button" onClick={() => onOpen(c)} data-testid={`talking-card-${c.id}`} className="group relative w-52 sm:w-60 aspect-[5/7] flex-shrink-0 mx-2 snap-center rounded-xl overflow-hidden bg-white border border-[#1B2A41]/15 shadow-md transition-transform duration-300 hover:scale-[1.04] hover:border-[#E2705A]">
      <img src={cardImg(c, "thumb")} alt={c.title || "Talking card"} loading="lazy" className="absolute inset-0 w-full h-full object-cover" />
      {c.title && <span className="absolute bottom-0 inset-x-0 bg-gradient-to-t from-[#121E2B]/85 to-transparent text-[#FDFBF7] text-xs font-semibold p-3 text-left">{c.title}</span>}
    </button>
  );

export const TalkingCards = () => {
  const { data = [] } = useCards();
  const { data: docs = [] } = useDocuments();
  const byKind = Object.fromEntries(docs.map((d) => [d.kind, d]));
  const [idx, setIdx] = useState(null);
  const trackRef = useRef(null);
  const cards = data.length ? data : PLACEHOLDERS;
  const open = idx !== null ? data[idx] : null;
  const step = (d) => setIdx((i) => (i + d + data.length) % data.length);

  useEffect(() => {
    const wanted = new URLSearchParams(window.location.search).get("card");
    if (!wanted || !data.length) return;
    const i = data.findIndex((c) => c.id === wanted);
    if (i >= 0) {
      setIdx(i);
      trackRef.current?.scrollIntoView({ block: "center" });
    }
  }, [data]);

  return (
    <section className="max-w-6xl mx-auto px-5 sm:px-8 pb-14" data-testid="talking-cards-section">
      <div className="flex flex-wrap items-end justify-between gap-3 mb-4">
        <div>
          <p className="font-type text-xs uppercase tracking-[0.3em] text-[#5A7355] mb-1">Talking cards</p>
          <h2 className="font-type font-bold text-base md:text-lg text-[#1B2A41] uppercase tracking-wider">Flick through, read, share or print</h2>
        </div>
        {data.length > 0 && (
          <div className="flex flex-wrap gap-2" data-testid="talking-cards-downloads">
            <VariantButton kind="cards_digital" doc={byKind.cards_digital} mode="digital" fallbackHref={`${API}/cards/pack.pdf`} />
            <VariantButton kind="cards_print" doc={byKind.cards_print} mode="print" />
          </div>
        )}
      </div>
      <div className="relative -mx-5 sm:-mx-8" data-testid="talking-cards-marquee">
        <StripArrow dir={-1} onClick={() => scrollStrip(trackRef, -1)} testId="cards-strip-prev" label="Previous cards" className="left-1 sm:left-3" />
        <StripArrow dir={1} onClick={() => scrollStrip(trackRef, 1)} testId="cards-strip-next" label="Next cards" className="right-1 sm:right-3" />
        <div ref={trackRef} data-testid="cards-strip" className="flex overflow-x-auto snap-x snap-mandatory scroll-smooth px-11 sm:px-14 py-3 no-scrollbar">
          {cards.map((c, i) => <Tile key={c.id} c={c} onOpen={() => setIdx(i)} />)}
        </div>
      </div>

      <Dialog open={!!open} onOpenChange={(o) => !o && setIdx(null)}>
        <DialogContent data-testid="talking-card-viewer" className="p-0 bg-[#121E2B] border-[#E2705A]/40 max-w-[min(94vw,720px)] overflow-hidden [&>button]:hidden">
          <DialogTitle className="sr-only">{open?.title || "Talking card"}</DialogTitle>
          {open && (
            <div className="relative min-h-[50vh]">
              <img src={cardImg(open, "full")} alt={open.title || "Talking card"} className="w-full max-h-[80vh] object-contain bg-[#121E2B]" />
              <button onClick={() => setIdx(null)} data-testid="talking-card-close" className="absolute top-3 right-3 w-9 h-9 rounded-full bg-[#121E2B]/80 text-[#FDFBF7] flex items-center justify-center hover:bg-[#E2705A]"><X className="w-4 h-4" /></button>
              {data.length > 1 && (
                <>
                  <StripArrow dir={-1} onClick={() => step(-1)} testId="talking-card-prev" label="Previous card" className="left-3" />
                  <StripArrow dir={1} onClick={() => step(1)} testId="talking-card-next" label="Next card" className="right-3" />
                </>
              )}
              <div className="flex flex-wrap items-center justify-between gap-3 px-4 py-3 text-xs text-[#FDFBF7]/80">
                <span data-testid="talking-card-counter">{idx + 1} / {data.length}{open.title ? ` · ${open.title}` : ""}</span>
                <div className="flex items-center gap-3">
                  <CardShareButtons card={open} index={idx} total={data.length} />
                  <a href={cardImg(open, "full")} download={open.original_filename || "talking-card"} data-testid="talking-card-download" className="inline-flex items-center gap-1 text-[#E2705A] hover:underline"><Download className="w-3.5 h-3.5" /> Download</a>
                </div>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </section>
  );
};
